import { useState, useMemo, useEffect } from "react";
import { Table, Form, Alert } from "react-bootstrap";
import { useTable, useRowSelect } from "react-table";

const CustomerCreditsTable = ({
    customerCredits,
    onTotalChange,
    remainingInvoiceAmount,
}) => {
    const [amountsToUse, setAmountsToUse] = useState({});
    const [validationError, setValidationError] = useState("");

    const invoiceAmount =
        remainingInvoiceAmount != null ? Number(remainingInvoiceAmount) : null;
    const data = useMemo(() => customerCredits || [], [customerCredits]);

    const columns = useMemo(
        () => [
            {
                Header: "S/n",
                accessor: (row, index) => index + 1,
                id: "serialNumber",
            },
            {
                Header: "CN Reference No.",
                accessor: "cn_number",
                Cell: ({ value }) => value ?? "N/A",
            },
            {
                Header: "Amount",
                accessor: "amount",
            },
            {
                Header: "Remaining Amount",
                accessor: "remaining_amount",
                Cell: ({ value }) => value ?? "",
            },
            {
                Header: "Amount to be Used",
                id: "amountToUse",
            },
            {
                Header: "Type",
                accessor: "type",
                Cell: ({ value }) =>
                    value?.toUpperCase() === "CREDIT" ? value : "Used",
            },
            {
                Header: "Remarks",
                accessor: "remarks",
            },
            {
                Header: "Invoices Used",
                accessor: "invoice_number",
                Cell: ({ value }) => value || "N/A",
            },
            {
                Header: "Date",
                accessor: "created_at",
                Cell: ({ value }) =>
                    new Date(value).toISOString().split("T")[0],
            },
        ],
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        selectedFlatRows,
    } = useTable(
        {
            columns,
            data,
        },
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <Form.Check
                            type="checkbox"
                            {...getToggleAllRowsSelectedProps()}
                        />
                    ),
                    Cell: ({ row }) => (
                        <Form.Check
                            type="checkbox"
                            {...row.getToggleRowSelectedProps()}
                        />
                    ),
                },
                ...columns,
            ]);
        }
    );

    // Auto-fill amounts when selection changes
    useEffect(() => {
        if (invoiceAmount == null || selectedFlatRows.length === 0) {
            setAmountsToUse({});
            setValidationError(""); // Clear error when no selection
            return;
        }

        const selectedRowIds = new Set(selectedFlatRows.map((row) => row.id));

        setAmountsToUse((prev) => {
            const updated = {};
            Object.keys(prev).forEach((rowId) => {
                if (selectedRowIds.has(rowId)) {
                    updated[rowId] = prev[rowId];
                }
            });
            return updated;
        });

        const newAmounts = { ...amountsToUse };
        let remainingToAllocate = invoiceAmount;

        selectedFlatRows.forEach((row) => {
            if (newAmounts[row.id]) {
                remainingToAllocate -= parseFloat(newAmounts[row.id]) || 0;
            }
        });

        const sortedRows = [...selectedFlatRows].sort(
            (a, b) => a.index - b.index
        );

        for (const row of sortedRows) {
            if (newAmounts[row.id]) continue;

            if (remainingToAllocate <= 0) {
                newAmounts[row.id] = "0";
                continue;
            }

            const creditRemaining =
                row.original.remaining_amount != null
                    ? Number(row.original.remaining_amount)
                    : Number(row.original.amount);

            const amountToAllocate = Math.min(
                remainingToAllocate,
                creditRemaining
            );

            newAmounts[row.id] = amountToAllocate.toFixed(2);
            remainingToAllocate -= amountToAllocate;
        }

        setAmountsToUse(newAmounts);
        setValidationError(""); // Clear error after auto-fill
    }, [selectedFlatRows.map((row) => row.id).join(","), invoiceAmount]);

    const handleAmountChange = (rowId, value) => {
        const regex = /^\d*\.?\d{0,2}$/;

        if (!regex.test(value) && value !== "") {
            return;
        }

        if (invoiceAmount == null) {
            setAmountsToUse((prev) => ({
                ...prev,
                [rowId]: value,
            }));
            setValidationError("");
            return;
        }

        const otherRowsTotal = selectedFlatRows.reduce((sum, row) => {
            if (row.id === rowId) return sum;
            const amount = parseFloat(amountsToUse[row.id]) || 0;
            return sum + amount;
        }, 0);

        const newValue = value ? parseFloat(value) : 0;
        const newTotal = otherRowsTotal + newValue;

        const currentRow = selectedFlatRows.find((row) => row.id === rowId);
        const creditRemaining =
            currentRow?.original.remaining_amount != null
                ? Number(currentRow.original.remaining_amount)
                : Number(currentRow?.original.amount || 0);

        if (newValue > creditRemaining) {
            setValidationError(
                `Cannot exceed credit's remaining amount (${creditRemaining.toFixed(
                    2
                )})`
            );
            return;
        }

        if (newTotal > invoiceAmount) {
            const maxAllowed = invoiceAmount - otherRowsTotal;
            setValidationError(
                `Cannot exceed remaining invoice amount. Maximum allowed for this credit: ${maxAllowed.toFixed(
                    2
                )}`
            );
            return;
        }

        setValidationError("");
        setAmountsToUse((prev) => ({
            ...prev,
            [rowId]: value,
        }));
    };

    useEffect(() => {
        const selectedData = selectedFlatRows
            .map((row) => {
                const rawValue = amountsToUse[row.id];

                // Convert to cents (integer)
                const amountInCents = rawValue
                    ? Math.round(parseFloat(rawValue) * 100)
                    : 0;

                return {
                    id: row.original.id,
                    amountInCents,
                };
            })
            .filter((item) => item.amountInCents > 0);

        // Sanitize values to return two decimals only
        const totalInCents = selectedData.reduce(
            (sum, item) => sum + item.amountInCents,
            0
        );

        const total = totalInCents / 100;

        const invoiceAmountInCents =
            invoiceAmount != null ? Math.round(invoiceAmount * 100) : null;

        // Validation
        if (
            invoiceAmountInCents != null &&
            totalInCents > invoiceAmountInCents
        ) {
            setValidationError(
                `Total amount (${total.toFixed(
                    2
                )}) exceeds remaining invoice amount (${(
                    invoiceAmountInCents / 100
                ).toFixed(2)})`
            );

            onTotalChange?.(0, []);
        } else {
            setValidationError("");
            onTotalChange?.(
                total,
                selectedData.map((item) => ({
                    id: item.id,
                    amount: item.amountInCents / 100,
                }))
            );
        }
    }, [amountsToUse, selectedFlatRows, invoiceAmount]);

    return (
        <div>
            <Table responsive striped bordered {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps()}
                                    className={column.accessor != 'invoice_number' ? "text-nowrap" : ''}
                                >
                                    {column.render("Header")}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                        prepareRow(row);

                        const otherRowsTotal = selectedFlatRows.reduce(
                            (sum, r) => {
                                if (r.id === row.id) return sum;
                                const amount =
                                    parseFloat(amountsToUse[r.id]) || 0;
                                return sum + amount;
                            },
                            0
                        );

                        const creditRemaining =
                            row.original.remaining_amount != null
                                ? Number(row.original.remaining_amount)
                                : Number(row.original.amount || 0);

                        const maxFromInvoice =
                            invoiceAmount != null
                                ? invoiceAmount - otherRowsTotal
                                : null;
                        const maxForThisRow =
                            maxFromInvoice != null
                                ? Math.min(maxFromInvoice, creditRemaining)
                                : creditRemaining;

                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    if (cell.column.id === "amountToUse") {
                                        return (
                                            <td
                                                {...cell.getCellProps()}
                                                className="text-nowrap"
                                            >
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    disabled={!row.isSelected}
                                                    value={
                                                        amountsToUse[row.id] ||
                                                        ""
                                                    }
                                                    onChange={(e) =>
                                                        handleAmountChange(
                                                            row.id,
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="0.00"
                                                    step="0.01"
                                                    min="0"
                                                    max={maxForThisRow}
                                                    title={`Maximum: ${maxForThisRow.toFixed(
                                                        2
                                                    )}`}
                                                />
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            {...cell.getCellProps()}
                                            className={cell.column.accessor != 'invoice_number' ? "text-nowrap" : ''}
                                        >
                                            {cell.render("Cell")}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            {validationError && (
                <Alert variant="danger" className="mt-3 p-2">
                    {validationError}
                </Alert>
            )}
        </div>
    );
};

export { CustomerCreditsTable };
